/**
 * Carte KPI pour le dashboard admin
 * Supporte navigation via href ou action via onClick
 */

import { LucideIcon, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface AdminKPICardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  subtitle?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  /** URL de navigation au clic */
  href?: string;
  /** Action au clic (alternative à href) */
  onClick?: () => void;
  /** Texte indicatif affiché en bas de la carte */
  actionHint?: string;
}

export function AdminKPICard({
  title,
  value,
  icon: Icon,
  subtitle,
  variant = 'default',
  href,
  onClick,
  actionHint,
}: AdminKPICardProps) {
  const variantStyles = {
    default: 'bg-slate-100 text-slate-600',
    primary: 'bg-primary/10 text-primary',
    success: 'bg-green-100 text-green-600',
    warning: 'bg-amber-100 text-amber-600',
    danger: 'bg-red-100 text-red-600',
  };

  const isClickable = href || onClick;

  const cardContent = (
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold font-mono">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
          {isClickable && actionHint && (
            <p className="text-xs text-primary/70 mt-2 flex items-center gap-1">
              <ChevronRight className="h-3 w-3" />
              {actionHint}
            </p>
          )}
        </div>
        <div className={cn('p-3 rounded-lg', variantStyles[variant])}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  );

  // Si href, wrapper avec Link
  if (href) {
    return (
      <Link to={href} className="block">
        <Card className={cn(
          "transition-all duration-200",
          "cursor-pointer hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
        )}>
          {cardContent}
        </Card>
      </Link>
    );
  }

  // Si onClick, ajouter le handler
  if (onClick) {
    return (
      <Card
        className={cn(
          "transition-all duration-200",
          "cursor-pointer hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
        )}
        onClick={onClick}
      >
        {cardContent}
      </Card>
    );
  }

  // Sinon, carte non cliquable
  return (
    <Card className="hover:shadow-md transition-shadow">
      {cardContent}
    </Card>
  );
}
